/**
 * Types for normalized tracker API responses.
 *
 * These types correspond to the Pydantic models in the backend:
 * - ParameterSchema
 * - NormalizedTrackerInfo
 * - SourceTrackerTypes
 * - TrackerTypeResponse
 */

/**
 * Schema for a tracker parameter.
 * Describes the name, type, and constraints of a parameter.
 */
export interface ParameterSchema {
  /** Parameter name (e.g., "query", "limit") */
  name: string;
  /** Whether the parameter is required */
  required: boolean;
  /** Parameter type: "string", "integer", "boolean", "array" */
  type: "string" | "integer" | "boolean" | "array";
  /** Human-readable title */
  title?: string;
  /** Parameter description */
  description?: string;
  /** Example value */
  example?: string;
  /** Default value if not provided */
  default?: unknown;
  /** Enum values if the parameter has fixed options */
  enum?: string[];
  /** For array types, the type of items */
  items_type?: string;
}

/**
 * Complete info for a normalized tracker endpoint.
 * Includes the endpoint path, parameters, and chain info.
 */
export interface NormalizedTrackerInfo {
  /** API endpoint path (e.g., "/twitter/tweets/search") */
  endpoint: string;
  /** HTTP method (usually "GET") */
  method: string;
  /** List of parameters for this endpoint */
  parameters: ParameterSchema[];
  /** Pre-configured params when a search query is provided */
  configured_params?: Record<string, unknown>;
  /** Suggested tracker name (based on the query) */
  suggested_name?: string;
  /** Whether the endpoint supports boolean query syntax (AND, OR, etc.) */
  supports_boolean?: boolean;
  /** Whether this tracker chains to another endpoint */
  has_chain: boolean;
  /** Chain endpoint path (if has_chain is true) */
  chain_endpoint?: string;
  /** Parameter name for the chained endpoint's ID */
  chain_id_param?: string;
  /** Path in the response to get the ID for chaining */
  chain_id_path?: string;
}

/**
 * Tracker types available for a source.
 */
export interface SourceTrackerTypes {
  /** Source name (e.g., "twitter", "tiktok") */
  source: string;
  /** List of supported tracker types */
  types: string[];
  /** Whether this source is available in the gateway registry */
  available: boolean;
}

/**
 * Single tracker info with source and search term.
 * Used when boolean queries are decomposed into individual terms.
 */
export interface ExpandedTrackerInfo {
  /** Source name (e.g., "twitter", "tiktok") */
  source: string;
  /** The search term (after boolean decomposition if applicable) */
  search_term: string;
  /** Tracker info with configured_params for this specific term */
  info: NormalizedTrackerInfo;
}

/**
 * Response for a tracker type with all sources.
 * Returns a flat list of tracker entries, one per source/term combination.
 * When supports_boolean=false and the query contains boolean operators,
 * the query is decomposed into individual terms.
 */
export interface TrackerTypeResponse {
  /** Tracker type (e.g., "keywords", "persons") */
  type: string;
  /** Flat list of source/term combinations */
  sources: ExpandedTrackerInfo[];
}

/**
 * All available normalized tracker types (from backend API).
 * These are the types that map to specific source endpoints.
 */
export const TRACKER_TYPES = [
  "keywords",
  "hashtags",
  "persons",
  "groups",
  "locations",
  "products",
  "news",
  "videos",
  "images",
  "companies",
  "articles",
] as const;

export type TrackerTypeName = (typeof TRACKER_TYPES)[number];

/**
 * Special tracker types that are handled differently (UI-only, not from API).
 * - import: Import custom data
 * - advanced: Access all trackers without type filtering
 */
export const SPECIAL_TRACKER_TYPES = ["import", "advanced"] as const;

export type SpecialTrackerTypeName = (typeof SPECIAL_TRACKER_TYPES)[number];

/**
 * All tracker types (normalized + special).
 */
export const ALL_TRACKER_TYPES = [...TRACKER_TYPES, ...SPECIAL_TRACKER_TYPES] as const;

export type AllTrackerTypeName = TrackerTypeName | SpecialTrackerTypeName;

/**
 * Human-readable labels for tracker types.
 */
export const TRACKER_TYPE_LABELS: Record<AllTrackerTypeName, string> = {
  keywords: "Mots-clés",
  hashtags: "Hashtags",
  persons: "Personnes",
  groups: "Groupes",
  locations: "Géolocalisation",
  products: "Produits",
  news: "Actualités",
  videos: "Vidéos",
  images: "Images",
  companies: "Entreprises",
  articles: "Articles",
  import: "Importer",
  advanced: "Avancé",
};

/**
 * Descriptions for tracker types.
 */
export const TRACKER_TYPE_DESCRIPTIONS: Record<AllTrackerTypeName, string> = {
  keywords: "Recherche par mots-clés",
  hashtags: "Recherche par hashtags",
  persons: "Suivi de personnes ou comptes",
  groups: "Suivi de groupes ou communautés",
  locations: "Recherche par localisation",
  products: "Recherche de produits",
  news: "Recherche d'actualités",
  videos: "Recherche de vidéos",
  images: "Recherche d'images",
  companies: "Suivi d'entreprises",
  articles: "Recherche d'articles",
  import: "Importer vos propres données",
  advanced: "Accès à tous les trackers",
};

/**
 * Input labels for the search value field based on tracker type.
 */
export const TRACKER_TYPE_INPUT_LABELS: Record<TrackerTypeName, string> = {
  keywords: "Mot-clé à rechercher",
  hashtags: "Hashtag à suivre",
  persons: "Nom d'utilisateur ou personne",
  groups: "Nom du groupe ou communauté",
  locations: "Lieu ou adresse",
  products: "Nom du produit",
  news: "Sujet d'actualité",
  videos: "Titre ou sujet de vidéo",
  images: "Sujet de l'image",
  companies: "Nom de l'entreprise",
  articles: "Sujet de l'article",
};

/**
 * Placeholder examples for the search value field based on tracker type.
 */
export const TRACKER_TYPE_INPUT_PLACEHOLDERS: Record<TrackerTypeName, string> = {
  keywords: "ex: intelligence artificielle",
  hashtags: "ex: #AI ou AI (sans #)",
  persons: "ex: @elonmusk ou elonmusk",
  groups: "ex: r/MachineLearning",
  locations: "ex: Paris, France",
  products: "ex: iPhone 15",
  news: "ex: élections présidentielles",
  videos: "ex: tutoriel Python",
  images: "ex: paysage montagne",
  companies: "ex: OpenAI",
  articles: "ex: changement climatique",
};

/**
 * Default parameter name for the search value based on tracker type.
 * This is the most common parameter name used by scrapers for each type.
 * Note: Some scrapers may use different names, but this covers the majority.
 */
export const TRACKER_TYPE_SEARCH_PARAM: Record<TrackerTypeName, string> = {
  keywords: "keyword",
  hashtags: "keyword",
  persons: "keyword",
  groups: "keyword",
  locations: "query",
  products: "keyword",
  news: "query",
  videos: "query",
  images: "query",
  companies: "query",
  articles: "query",
};
